import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Checkbox, Flex, Modal, Result } from 'antd';

import { ContentLoader, ContentNoData, Notification, Number, OperationApproval } from 'src/Components';
import { CURRENCIES, SMS_TOKEN_TYPES, SIGN_CANCEL_TYPES, currencyTextValue, i18, numberFormat, translationGroups, trb } from 'src/Utilities';
import { OMCActions, OMCConstants, PMCActions } from 'src/Redux';
import moment from 'moment/moment';
import { PaymentError } from '../../components';
import { AlertNotification, Typography } from 'src/shared';
import { PayeeVerificationStatus } from 'src/Components/PayeeVerificationStatus/PayeeVerificationStatus';
import { getVopType } from 'src/Utilities/vop';
import { BlockedOutgoingPaymentsNotification } from '../../../../Components/BlockedOutgoingPaymentsNotification/BlockedOutgoingPaymentsNotification.tsx';

const maxCheckedRow = 30;
const messagesEndRef = React.createRef();

export const UnsignedPayments = ({ UOLR, sendUOLR }: any) => {
  const dispatch = useDispatch();
  const { PSUOR, sendPSUOR, sendSUORSMS } = useSelector((state: any) => state.OMCReducers);
  const { sendPTR, SDPVR } = useSelector((state: any) => state.PMCReducers);
  const { sendATR, MALR } = useSelector((state: any) => state.UACReducers);
  const isOutgoingPaymentsBlocked = MALR.ManagedAccounts.find((item: any) => item.IsActive)?.OutgoingPaymentsBlocked;
  const { language } = useSelector((state: any) => state.languageReducer);
  const refError = useRef(null);
  const [state, setState] = useState({
    selectedRowKeys: [] as any,
    send: false,
    modalVisible: false,
    itemInst: null,
    isError: false,
    isErrorGlobal: false,
    errors: [] as any,
    currentStep: 0,
    approve: false,
    approveInst: false,
    RQ_ID: null,
    selectAllChecked: false,
    AuthType: null,
    Code: null,
    submitting: false,
  });

  const loading = sendUOLR || sendATR || sendPTR || state.send;

  const loadData = () => {
    dispatch(OMCActions.postUOLR() as any);
  };

  const selectRow = (checked: any, id: any) => {
    if (checked) {
      setState({
        ...state,
        selectedRowKeys: [...state.selectedRowKeys, id] as any,
      });
    } else {
      setState({
        ...state,
        selectedRowKeys: state.selectedRowKeys.filter((e: any) => e !== id),
      });
    }
  };

  const clickDeleteButton = () => {
    const { selectedRowKeys } = state;

    dispatch(OMCActions.postDUOR({ ItemsIDs: selectedRowKeys }) as any).then((response: any) => {
      if (response.type === OMCConstants.POST_OMC_DUOR_SUCCESS) {
        if (response.payload.IsSuccessfull) {
          Notification({
            type: 'success',
            message: trb(i18.MessageTitles.DeletedSuccessUnsignedPaymentsTitle),
            description: trb(i18.Messages.DeletedSuccessUnsignedPaymentsDesc),
          });
          dispatch(OMCActions.postUOLR() as any);
        } else {
          Notification({
            type: 'error',
            message: trb(i18.Messages.ResponseError),
          });
        }
      }
      setState({ ...state, selectedRowKeys: [], errors: [], isError: false });
    });
  };

  const clickApproveButton = (itemInst: any = null) => {
    const selectedRowKeys = itemInst ? [itemInst.ID] : state.selectedRowKeys,
      { Operations } = UOLR;

    setState({
      ...state,
      send: true,
      itemInst,
      approve: false,
      approveInst: false,
      errors: null as any,
    });

    const checkFeeData = {
      GroupPayments: Operations.filter((item: any) => selectedRowKeys.includes(item.ID)).map((item: any) => ({
        ID: String(item.ID),
        Description: item.Description,
        Amount: item.Amount[0].Value,
      })),
    };

    dispatch(OMCActions.resetPSUOR() as any);
    dispatch(PMCActions.postSDPVR(checkFeeData) as any);
  };

  useEffect(() => {
    if (SDPVR?.IsSuccessfull) {
      setState({
        ...state,
        modalVisible: true as any,
        send: false,
        approve: false as any,
        approveInst: false,
        isError: false,
        errors: null as any,
      });
    } else if (SDPVR?.IsSuccessfull === false) {
      setState({
        ...state,
        approve: false,
        approveInst: false,
        RQ_ID: null,
        AuthType: null,
        Code: null,
        send: false,
        submitting: false,
        selectedRowKeys: [],
      });
      showErrors(SDPVR);
    }
  }, [SDPVR]);

  useEffect(() => {
    let newState = { ...state, send: false };

    if (PSUOR?.IsSuccessfull) {
      if (PSUOR?.ErrorMessage) {
        showErrors(PSUOR);
        return;
      }

      const rq = PSUOR?.AuthType && SMS_TOKEN_TYPES.includes(PSUOR?.AuthType.toUpperCase()) ? PSUOR?.SMSToken : PSUOR?.iSignToken;

      newState = state.itemInst
        ? {
            ...state,
            approveInst: Boolean((state.itemInst as any)?.ID),
            AuthType: PSUOR?.AuthType,
            RQ_ID: rq,
            Code: PSUOR?.ControlCode,
            modalVisible: true,
          }
        : {
            ...state,
            approve: true,
            AuthType: PSUOR?.AuthType,
            RQ_ID: rq,
            Code: PSUOR?.ControlCode,
            modalVisible: true,
          };
    } else if (PSUOR?.IsSuccessfull === false) {
      showErrors(PSUOR);

      return;
    }

    setState(newState);
  }, [PSUOR]);

  const showErrors = (response: any) => {
    setState((prev: any) => ({
      ...prev,
      modalVisible: false,
      isError: true,
      send: false,
      errors: {
        errorList: response?.PaymentErrorsItems?.filter(
          (items: any, index: any) => index === response.PaymentErrorsItems.findIndex((item: any) => item.ErrorCode === items.ErrorCode)
        ) || [{ ErrorCode: response?.ErrorCode || 'Unknown' }],
      },
    }));

    if (response?.ErrorCode === 'PaymentCountryIncorrect') {
      loadData();
    }
  };

  const cancelApprove = (type: any, _errorCode: any, notificationType = 'warning') => {
    let message;

    switch (type) {
      case SIGN_CANCEL_TYPES.TIMEOUT:
        message = 'PaymentCanceledTimeout';
        break;
      case SIGN_CANCEL_TYPES.MANUAL:
        message = 'PaymentCanceledManual';
        break;
      case SIGN_CANCEL_TYPES.REJECTED:
        message = 'PaymentCanceledRejected';
        break;
      default:
        message = null;
        break;
    }

    if (message) {
      Notification({
        type: notificationType,
        message: i18.NotificationTitles[message as keyof typeof i18.NotificationTitles],
        description: i18.NotificationMessages[message as keyof typeof i18.NotificationMessages],
      });
    }

    setState({
      ...state,
      approve: false,
      approveInst: false,
      RQ_ID: null,
      AuthType: null,
      Code: null,
      send: false,
      submitting: false,
      isErrorGlobal: !message,
    });
  };

  const clickApproveModal = async () => {
    const { itemInst } = state as any;
    const selectedRowKeys = itemInst ? [itemInst.ID] : state.selectedRowKeys;

    setState({
      ...state,
      send: true,
    });

    const res = await dispatch(OMCActions.postPSUOR({ ItemsIDs: selectedRowKeys, Language: language }) as any);

    if (!res.payload?.IsSuccessfull) {
      setState({
        ...state,
        send: false,
      });

      cancelApprove(SIGN_CANCEL_TYPES.REJECTED, null, 'warning');
    }
  };

  const successApprove = () => {
    const { itemInst } = state as any;
    const selectedRowKeys = itemInst ? [itemInst.ID] : state.selectedRowKeys;

    dispatch(OMCActions.removeUOLRCompleted(selectedRowKeys) as any);

    setState({
      ...state,
      approve: false,
      approveInst: false,
      send: false,
      currentStep: state.currentStep + 1,
      selectedRowKeys: [],
      modalVisible: false,
    });

    Notification({
      type: 'success',
      message: i18.NotificationTitles.SuccessApproveUnsigned,
      description: i18.NotificationMessages.SuccessApproveUnsigned,
    });
  };

  const submitSmsApprove = (values: any) => {
    dispatch(OMCActions.postSUORSMS(values) as any).then((response: any) => {
      const ErrorCode = response?.payload?.ErrorCode;

      if (response.payload.IsSuccessfull) {
        successApprove();
      } else {
        cancelApprove(null, ErrorCode, 'error');
      }
    });
  };

  const submitMobileApprove = async () => {
    const { RQ_ID } = state;
    const response = await dispatch(OMCActions.postSUOR({ iSignToken: RQ_ID }) as any);
    const AuthType = response?.payload?.AuthType;
    const IsSuccessfull = response?.payload?.IsSuccessfull;
    const ErrorCode = response?.payload?.ErrorCode;

    if (AuthType === null && IsSuccessfull === false) {
      return true;
    }

    if (AuthType === 'false') {
      cancelApprove(null, ErrorCode);
    } else if (IsSuccessfull === true) {
      successApprove();
    }
    return false;
  };

  const checkCountMore = (disabled = true) => {
    if (disabled) {
      Notification({
        type: 'error',
        message: trb(i18.Messages.NoSignedPaymentErrorMuchMore),
        description: i18.NotificationMessages.NoSignedPaymentErrorMuchMore + `: ${maxCheckedRow}`,
      });
    }
  };

  const checkRow = (e: any, id = null) => {
    const isChecked = e.target.checked;

    selectRow(isChecked, id);
    if (!isChecked) {
      setState((prevState) => ({
        ...prevState,
        selectAllChecked: false,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        selectAllChecked: prevState.selectedRowKeys.length === Math.min(UOLR.Operations.length, maxCheckedRow),
      }));
    }
  };

  const checkAllRow = (e: any) => {
    const isChecked = e.target.checked;

    setState((prevState) => ({
      ...prevState,
      selectedRowKeys: isChecked ? UOLR.Operations.slice(0, maxCheckedRow).map((item: any) => item.ID) : [],
      selectAllChecked: isChecked,
    }));
  };

  const { modalVisible } = state,
    PaymentsTotalAmount = SDPVR?.PaymentsTotalAmount,
    PaymentsTotalCurrency = SDPVR?.GroupPayments?.[0]?.currency,
    PaymentsCount = SDPVR?.PaymentsCount;

  useEffect(() => {
    if (SDPVR?.ErrorCode || SDPVR?.ErrorCaptions?.length) {
      (refError?.current as any)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [SDPVR?.ErrorCaptions]);

  if (sendUOLR) {
    return <ContentLoader />;
  }

  if (!UOLR) {
    return <ContentNoData id="UnsignedPaymentsUOLR" />;
  }

  const { selectedRowKeys, isError, errors } = state;

  return (
    <>
      {modalVisible && (
        <Modal
          width={700}
          centered
          style={{ top: 0 }}
          styles={{
            body: {
              padding: 0,
              paddingBottom: 0,
            },
          }}
          open={modalVisible}
          title={
            <Typography.Title
              level={5}
              className="my-0"
            >
              {trb(state.itemInst ? i18.Labels.PaymentSubmitModalInstant : i18.Labels.UnsignedPaymentSubmitModal)}
            </Typography.Title>
          }
          onCancel={() => {
            if (sendPSUOR || sendSUORSMS) {
              return;
            }

            if (state.isErrorGlobal) {
              loadData();
            }

            setState({ ...state, modalVisible: false, itemInst: null, send: false, isErrorGlobal: false });
          }}
          closable={true}
          footer={[
            <Button.Group key="footer">
              {!state.isErrorGlobal && (
                <Button
                  id="UnsignedPaymentsSubmitButton"
                  type="primary"
                  disabled={sendPSUOR || loading || state.approve || state.approveInst}
                  onClick={clickApproveModal}
                >
                  {trb(i18.Buttons.Approve)}
                </Button>
              )}
              <Button
                id="UnsignedPaymentsCancelButton"
                type="default"
                disabled={sendPSUOR || loading || state.approve || state.approveInst}
                style={{ marginLeft: 0 }}
                onClick={() => {
                  if (sendPSUOR || sendSUORSMS) {
                    return;
                  }

                  if (state.isErrorGlobal) {
                    loadData();
                  }

                  setState({
                    ...state,
                    modalVisible: false,
                    itemInst: null,
                    approve: false,
                    approveInst: false,
                    RQ_ID: null,
                    AuthType: null,
                    Code: null,
                    send: false,
                    submitting: false,
                    isErrorGlobal: false,
                  });
                }}
              >
                {state.isErrorGlobal ? trb(i18.Buttons.Close) : trb(i18.Buttons.Cancel)}
              </Button>
            </Button.Group>,
          ]}
        >
          {state.isErrorGlobal ? (
            <div className="px-0 pb-3 p-md-3">
              <Result
                status="error"
                title={trb(i18.ErrorTitles.Unknown)}
                subTitle={trb(i18.ErrorDescriptions.Unknown)}
              />
            </div>
          ) : (
            <div className="px-0 pb-3 p-md-3">
              {SDPVR?.Report?.map(({ Amount, Commission, CommissionCurrency, Currency, Quantity, ExecutionDate, CutOffTime }: any, index: any) => (
                <div
                  className="row"
                  key={index}
                >
                  <div className="col-8 col-md-3 py-1 text-black-50 my-auto d-flex justify-content-between align-items-center">{trb(i18.Labels.PaymentsCount)}</div>
                  <div className="col-4 col-md-1 py-1 py-md-2 text-right eb_fw_600 text-md-left text-black-70 my-auto px-10">
                    <Number
                      value={Quantity}
                      format={numberFormat().number}
                    />
                  </div>
                  <div className="col-12 col-md-4 py-1 text-black-50 my-auto d-flex justify-content-between align-items-center">
                    {trb(i18.Labels.PaymentsTotalAmount)}
                    <span className="ext-right eb_fw_600 text-md-left text-black-70 my-auto px-0">
                      <Number value={Amount} />
                      {` ${Currency}`}
                    </span>
                  </div>
                  <div className="col-12 col-md-4 py-1 text-black-50 my-auto d-flex justify-content-between align-items-center">
                    {trb(i18.Labels.PaymentsTotalCommFee)}
                    <span className="py-1 py-md-2 text-right eb_fw_600 text-md-left text-black-70 my-auto px-0">
                      <Number value={Commission} />
                      {` ${CommissionCurrency}`}
                    </span>
                  </div>
                  <div className="col-12 col-md-4 py-1 text-black-50 my-auto">
                    {ExecutionDate ? `${trb(i18.Labels.ExecutionDateValue, { executionDate: moment(ExecutionDate).format('YYYY-MM-DD') })}` : ''}
                  </div>
                  <div className="col-12 col-md-5 py-1 text-black-50 my-auto">{CutOffTime ? `${trb(i18.Labels.ExecutionDateCutOff, { sameDayCutOff: CutOffTime })}` : ''}</div>
                </div>
              ))}
              {sendPSUOR ? <ContentLoader /> : null}
            </div>
          )}
          {state.approve && (
            <div className="pb-3">
              <OperationApproval
                RQ_ID={state.RQ_ID}
                AuthType={state.AuthType}
                code={state.Code}
                cancelApprove={cancelApprove}
                successApprove={successApprove}
                submitSmsApprove={submitSmsApprove}
                submitMobileApprove={submitMobileApprove}
                approveCount={PaymentsCount}
                loading={sendPSUOR || sendSUORSMS}
              />
            </div>
          )}
          {state.approveInst && (
            <div className="pb-3">
              <OperationApproval
                RQ_ID={state.RQ_ID}
                AuthType={state.AuthType}
                code={state.Code}
                cancelApprove={cancelApprove}
                successApprove={successApprove}
                submitSmsApprove={submitSmsApprove}
                submitMobileApprove={submitMobileApprove}
                approveSum={{
                  sum: PaymentsTotalAmount,
                  currency: PaymentsTotalCurrency,
                }}
                loading={sendPSUOR || sendSUORSMS}
              />
            </div>
          )}
        </Modal>
      )}
      <div ref={refError}>
        {isError && SDPVR?.ErrorCaptions.length > 0 && <PaymentError errors={SDPVR?.ErrorCaptions} />}
        {isError && errors?.errorList?.length > 0 && (
          <div className="mb-4">
            <AlertNotification
              alertClassName="w-100"
              description={
                errors?.errorList?.length > 1 ? (
                  <ul className="mb-0">{errors?.errorList?.map((item: any, index: any) => <li key={index}>{trb(translationGroups.ErrorCode + item.ErrorCode)}</li>)}</ul>
                ) : (
                  errors?.errorList?.map((item: any, index: any) => <div key={index}>{trb(translationGroups.ErrorCode + item.ErrorCode)}</div>)
                )
              }
              type="info"
            />
          </div>
        )}
      </div>
      {!UOLR?.Operations?.length && (
        <div className="mb-2 w-100">
          {!!UOLR?.Operations?.length && <Typography.Title className="eb_fs_16 eb_fw_400">{trb(i18.Labels.UnsignedWithVOP)}</Typography.Title>}
          <ContentNoData id="UnsignedPaymentsOperations" />
        </div>
      )}
      {!!UOLR?.Operations?.length && (
        <>
          {isOutgoingPaymentsBlocked && <BlockedOutgoingPaymentsNotification />}
          <div className="mb-2 w-100">
            <div className="eb_table_wrapper eb_table_nosigned_payment eb_table_wrapper_bottom_lined mb-2">
              <div
                id="UnsignedPaymentsTable"
                className="eb_table"
              >
                <div className="row eb_table_head d-flex d-md-flex">
                  <div className="col-12 col-md-2 px-0 px-md-3 py-1 py-md-2 text-nowrap d-none d-md-block">
                    <Checkbox
                      id="UnsignedPaymentsAllCheckboxLG"
                      checked={state.selectAllChecked && selectedRowKeys.length > 0}
                      className="mr-2"
                      disabled={loading}
                      onChange={checkAllRow}
                    />
                    {trb(i18.Labels.Date)}
                  </div>
                  <div className="col-1 text-nowrap px-0 d-block d-md-none">
                    <Checkbox
                      id="UnsignedPaymentsAllCheckboxMD"
                      checked={state.selectAllChecked && selectedRowKeys.length > 0}
                      className="mr-2"
                      disabled={loading}
                      onChange={checkAllRow}
                    />
                    {trb(i18.Labels.SelectAll)}
                  </div>
                  <div className="col-3 d-none d-md-block">{trb(i18.Labels.SenderData)}</div>
                  <div className="col-3 d-none d-md-block">{trb(i18.Labels.ReceivedData)}</div>
                  <div className="col-2 d-none d-md-block">{trb(i18.Labels.PaymentDescription)}</div>
                  <div className="col-2 text-right d-none d-md-block">{trb(i18.Labels.Amount)}</div>
                </div>
                {UOLR.Operations?.map((item: any, index: any) => {
                  const disabled = selectedRowKeys.length === maxCheckedRow && !selectedRowKeys.includes(item?.ID);

                  const VopResponseType = getVopType(item.VopResponseType, item.VopDetails);

                  return (
                    <React.Fragment key={index}>
                      <div
                        id={`UnsignedPaymentsTR${index}`}
                        className="row eb_table_row text-word-wrap py-1 py-md-0 overflow-hidden"
                      >
                        <div
                          className="col-12 col-md-2 px-0 px-md-3 py-1 py-md-2 text-nowrap"
                          onClick={() => checkCountMore(disabled)}
                        >
                          <div className="row">
                            <div className="d-none d-md-block col-1 col-md-12 mb-2">
                              <Checkbox
                                id={`UnsignedPaymentsTR${index}CheckboxLG`}
                                checked={selectedRowKeys.includes(item?.ID)}
                                className="mr-2"
                                disabled={loading || disabled}
                                onChange={(e) => checkRow(e, item.ID)}
                              />
                              {item.DateFormatted}
                            </div>
                            <div
                              style={{ position: 'relative', left: 15 }}
                              className="d-block d-md-none col-1 col-md-12 mb-2 px-0"
                            >
                              <Checkbox
                                id={`UnsignedPaymentsTR${index}CheckboxMD`}
                                checked={selectedRowKeys.includes(item.ID)}
                                disabled={loading || disabled}
                                onChange={(e) => checkRow(e, item.ID)}
                              />
                            </div>
                            <div className="col-4 mb-2 text-black-50 d-md-none">{trb(i18.Labels.Date)}</div>
                            <div className="col-7 d-md-none mb-2">
                              <span className="eb_fw_300 unassigned-payments-right-value">{item.DateFormatted}</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-3 px-0 px-md-3 py-1 py-md-2">
                          <div className="row">
                            <div className="col-1 mb-2 d-md-none px-0" />
                            <div className="col-11 mb-2 d-block d-md-none text-black-50">{trb(i18.Labels.SenderData)}</div>
                            <div className="col-1 mb-2 d-md-none px-0" />
                            <div className="col-11 col-md-12 mb-2">
                              <div>{item.SenderName}</div>
                              <div className="eb_fw_300">{item.SenderAccount}</div>
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-3 px-0 px-md-3 py-1 py-md-2">
                          <div className="row">
                            <div className="col-1 mb-2 d-md-none px-0" />
                            <div className="col-11 mb-2 d-block d-md-none text-black-50">{trb(i18.Labels.ReceivedData)}</div>
                            <div className="col-1 mb-2 d-md-none px-0" />
                            <div className="col-11 col-md-12 mb-2">
                              <div>
                                {VopResponseType ? (
                                  <PayeeVerificationStatus
                                    VopResponseType={VopResponseType}
                                    ReceiverName={item.ReceiverName}
                                    MatchedName={item.VopCloseMatchName}
                                  />
                                ) : (
                                  item.ReceiverName
                                )}
                              </div>
                              <div className="eb_fw_300">{item.ReceiverAccount}</div>
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-2 px-0 px-md-3 py-1 py-md-2">
                          <div className="row">
                            <div className="col-1 mb-2 d-md-none px-0" />
                            <div className="col-11 mb-2 d-block d-md-none text-black-50">{trb(i18.Labels.PaymentDescription)}</div>
                            <div className="col-1 mb-2 d-md-none px-0" />
                            <div className="col-11 col-md-12 mb-2">
                              <div>{item.Description}</div>
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-2 px-0 px-md-3 py-1 py-md-2 text-right">
                          <div className="row text-start text-md-end">
                            <div className="col-1 mb-2 d-md-none px-0" />
                            <div className="col-4 mb-2 d-block d-md-none text-black-50">{trb(i18.Labels.Amount)}</div>
                            <div className="col-7 col-md-12 text-nowrap text-primary eb_fw_600">
                              <div className="unassigned-payments-right-value">{currencyTextValue(item.Amount[0].Value, item.Amount[0].Currency || CURRENCIES.EUR)}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
          <Flex
            ref={messagesEndRef as any}
            justify="end"
            gap="small"
            wrap="wrap"
            className="mb-2"
          >
            <Button
              id="UnsignedPaymentsDeleteButton"
              onClick={clickDeleteButton}
              disabled={selectedRowKeys.length <= 0 || modalVisible || loading}
              className="col-12 col-md-auto"
              size="large"
            >
              {trb(i18.Buttons.DeleteSelectedPayments)}
            </Button>
            <Button
              id="UnsignedPaymentsApproveButton"
              onClick={() => clickApproveButton()}
              disabled={selectedRowKeys.length <= 0 || modalVisible || loading || isOutgoingPaymentsBlocked}
              className="col-12 col-md-auto"
              size="large"
              type={selectedRowKeys.length ? 'primary' : 'default'}
            >
              {trb(i18.Buttons.SignSelectedPayments)}
            </Button>
          </Flex>
          {loading && !state.approve && !state.approveInst && !modalVisible && <ContentLoader style={{ backgroundColor: 'white', paddingTop: 30 }} />}
        </>
      )}
    </>
  );
};
